import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { enqueueSnackbar } from 'notistack';

import { Assignment, Enrollment } from '../../../../../models';
import { HighlightColor } from '../../../../../models/HighlightColor';
import { ItemType } from '../../../../../models/ItemType';
import { myLocalStorage } from '../../../../../myLocalStorage';
import { UpdateItemResponse, createItem } from '../../../../../network/useNetwork';
import { displayDate } from '../../../../../utils/dates';
import { ExpandIcon } from '../../../../Icons';

export interface ExtendedDueDateDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean, needsUpdate: boolean) => void;
  assignment?: Assignment;
  enrollments: Enrollment[];
}

export enum TimeDelimiter {
  Days = 'Days',
  Hours = 'Hours',
}

enum ApplyTo {
  SelectAll = 'SelectAll',
  SelectIndividual = 'SelectIndividual',
}

const subtractTimezoneOffset = (utcTime: Date) => {
  const tzoffset = utcTime.getTimezoneOffset() * 60000;
  const localTime = new Date(utcTime.getTime() - tzoffset);
  return localTime;
};

export default function ExtendedDueDateDialog(props: ExtendedDueDateDialogProps) {
  const { assignment, enrollments, isOpen, setIsOpen } = props;

  const theme = useTheme();
  const primaryMain = theme.palette.primary.main;
  const { t } = useTranslation();
  const [value, setValue] = useState<number>(0);
  const [timeDelimiter, setTimeDelimiter] = useState<TimeDelimiter>(TimeDelimiter.Hours);
  const [isSaving, setIsSaving] = useState(false);
  const [applyTo, setApplyTo] = useState<ApplyTo>(ApplyTo.SelectIndividual);
  const [timeDelimiterAnchorEl, setTimeDelimiterAnchorEl] = useState<any>(null);
  const me = myLocalStorage.getItem('person');

  const handleTimeChange = (value: number) => {
    setValue(value);
  };

  const addTimezoneOffset = (utcTime: Date) => {
    const tzoffset = utcTime.getTimezoneOffset() * 60000;
    const localTime = new Date(utcTime.getTime() + tzoffset);
    return localTime;
  };

  const handleShowTimeDelimiterMenu = (event: any) => {
    setTimeDelimiterAnchorEl(event.currentTarget);
  };

  const handleTimeDelimiterChange = (timeDelimiter: TimeDelimiter) => () => {
    setTimeDelimiter(timeDelimiter);
    setTimeDelimiterAnchorEl(null);
  };

  const displaySelectedEnrollments = () => {
    let label = '';

    if (enrollments.length === 1) {
      label = enrollments[0]?.person?.firstName + ' ' + enrollments[0]?.person?.lastName;
    } else if (enrollments.length === 2) {
      label =
        enrollments[0]?.person?.firstName +
        ' ' +
        enrollments[0]?.person?.lastName +
        ' and ' +
        enrollments[1]?.person?.firstName +
        ' ' +
        enrollments[1]?.person?.lastName;
    } else {
      label =
        enrollments[0]?.person?.firstName +
        ' ' +
        enrollments[0]?.person?.lastName +
        ' + ' +
        (enrollments.length - 1) +
        ' others';
    }

    label += ' selected.';

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          p: 2,
          backgroundColor: alpha(HighlightColor.BLUE, 0.3),
        }}
      >
        <Typography variant="caption">{label}</Typography>
      </Box>
    );
  };

  const handleApplyToChange = (applyTo: ApplyTo) => {
    setApplyTo(applyTo);
  };

  const handleSave = () => {
    setIsSaving(true);

    const assignmentId = assignment ? (applyTo === ApplyTo.SelectAll ? 0 : assignment.id!) : 0;

    const extendedDueDates: any = {};
    extendedDueDates[assignmentId] = {
      timeDelimiter,
      value: Number(value),
    };

    createItem(
      ItemType.BulkExtendedDueDate,
      {
        enrollmentIds: enrollments.map((enrollment: Enrollment) => enrollment.id),
        extendedDueDates,
      },
      (response: UpdateItemResponse) => {
        const { isSuccess, message } = response;

        if (isSuccess) {
          setIsOpen(false, true);
          enqueueSnackbar(t('success'), { variant: 'success' });
        } else {
          enqueueSnackbar(message, { variant: 'error' });
        }

        setIsSaving(false);
      }
    );
  };

  let newGradeDate = new Date(assignment?.gradeAt ?? new Date());
  if (timeDelimiter === TimeDelimiter.Days) {
    newGradeDate = new Date(newGradeDate.getTime() + value * 24 * 60 * 60 * 1000);
  } else if (timeDelimiter === TimeDelimiter.Hours) {
    newGradeDate.setHours(newGradeDate.getHours() + value);
  }

  return (
    <>
      <Dialog open={isOpen} onClose={() => setIsOpen(false, false)} fullWidth>
        <DialogTitle
          sx={{
            backgroundColor: primaryMain,
            color: '#fff',
            p: 2,
          }}
        >
          {t('extendDueDate')}
        </DialogTitle>

        <DialogContent sx={{ m: 0, p: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {displaySelectedEnrollments()}

            <Box sx={{ display: 'flex', flexDirection: 'row', p: 2 }}>
              <TextField
                label={t('moreTime')}
                value={value}
                type="number"
                onChange={(e: any) => handleTimeChange(e.target.value)}
                variant="outlined"
                sx={{ backgroundColor: '#fff', mr: 2 }}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />

              <Button
                onClick={handleShowTimeDelimiterMenu}
                endIcon={<ExpandIcon />}
                variant="outlined"
                sx={{ textTransform: 'none' }}
              >
                {timeDelimiter}
              </Button>
            </Box>

            {!assignment && (
              <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1, ml: 2, mb: 1 }}>
                <Typography sx={{ color: '#404040' }} variant="caption">
                  Extended dates will apply to all assignments.
                </Typography>
              </Box>
            )}

            {assignment && (
              <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1, ml: 2 }}>
                <Typography sx={{ color: '#404040' }} variant="caption">
                  New due date: {displayDate(newGradeDate, 'en', false)}
                </Typography>
              </Box>
            )}

            {assignment && (
              <Box sx={{ display: 'flex', flexDirection: 'row', p: 2 }}>
                <FormControl>
                  <RadioGroup
                    aria-labelledby="choose-apply-to-one-or-all-assignments"
                    name="choose-apply-to-one-or-all-assignments"
                    defaultValue={applyTo}
                    onChange={(event: any) => handleApplyToChange(event?.target.value)}
                  >
                    <FormControlLabel
                      control={<Radio />}
                      value={ApplyTo.SelectIndividual}
                      label={t('applyToThisAssignmentOnly')}
                    />

                    <FormControlLabel
                      control={<Radio />}
                      value={ApplyTo.SelectAll}
                      label={t('applyToAllAssignments')}
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            m: 0,
            p: 2,
            backgroundColor: '#eee',
            borderTop: '1px solid #c0c0c0',
          }}
        >
          <Button
            sx={{
              textTransform: 'none',
              backgroundColor: '#fff',
            }}
            variant="outlined"
            onClick={() => setIsOpen(false, false)}
          >
            {t('cancel')}
          </Button>

          <Button
            disabled={isSaving || value === undefined}
            sx={{ textTrasnform: 'none' }}
            variant="contained"
            onClick={handleSave}
          >
            {isSaving && <CircularProgress size={20} sx={{ color: primaryMain }} />}
            {!isSaving && (
              <Typography variant="button" sx={{ textTransform: 'none' }}>
                {t('save')}
              </Typography>
            )}
          </Button>
        </DialogActions>
      </Dialog>
      {timeDelimiterAnchorEl && (
        <Menu
          anchorEl={timeDelimiterAnchorEl}
          open={Boolean(timeDelimiterAnchorEl)}
          onClose={() => setTimeDelimiterAnchorEl(null)}
        >
          <MenuItem onClick={handleTimeDelimiterChange(TimeDelimiter.Hours)}>
            {TimeDelimiter.Hours}
          </MenuItem>

          <MenuItem onClick={handleTimeDelimiterChange(TimeDelimiter.Days)}>
            {TimeDelimiter.Days}
          </MenuItem>
        </Menu>
      )}
    </>
  );
}
